import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import {
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../services/notificationService";

const Notifications = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [filters, setFilters] = useState({ startDate: "", endDate: "" });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
  const hasFilters = Boolean(filters.startDate || filters.endDate);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getNotifications({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        limit: 0,
      });
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[Notifications] load error", err);
      setError("No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, [filters.endDate, filters.startDate]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => setFilters({ startDate: "", endDate: "" });

  const formatNotificationDate = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch {
      return "";
    }
  };

  const updateAsReadLocally = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (String(n.id || n._id) === String(id) ? { ...n, read: true } : n))
    );
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;
    const id = notification.id || notification._id;

    try {
      if (!notification.read && id) {
        await markNotificationAsRead(id);
        updateAsReadLocally(id);
      }
    } catch (err) {
      console.error("No se pudo marcar la notificación como leída", err);
    }

    if (notification.payload?.closureId) {
      navigate(`/turnos/${notification.payload.closureId}`);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("No se pudieron marcar las notificaciones", err);
    }
  };

  return (
    <Box m="20px" sx={{ pb: isMobile ? 2 : 0 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        mb={3}
      >
        <Header title="Notificaciones" subtitle="Histórico y avisos importantes" />
        <Button
          startIcon={<DoneAllIcon />}
          variant="contained"
          color="secondary"
          onClick={handleMarkAll}
          disabled={!unreadCount}
        >
          Marcar todo como leído
        </Button>
      </Stack>

      <Paper
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-end">
          <TextField
            label="Desde"
            type="date"
            value={filters.startDate}
            onChange={handleFilterChange("startDate")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Hasta"
            type="date"
            value={filters.endDate}
            onChange={handleFilterChange("endDate")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 180 }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={loadNotifications} startIcon={<NotificationsOutlinedIcon />}>
              Aplicar filtros
            </Button>
            <Button variant="text" color="secondary" onClick={handleClearFilters}>
              Limpiar
            </Button>
          </Stack>
        </Stack>
        {hasFilters && (
          <Typography variant="caption" color={colors.grey[200]} display="block" mt={1.5}>
            Filtrando por rango de fechas seleccionado
          </Typography>
        )}
      </Paper>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box display="flex" alignItems="center" gap={1.5}>
          <CircularProgress size={20} />
          <Typography variant="body1">Cargando notificaciones...</Typography>
        </Box>
      ) : notifications.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            backgroundColor: colors.primary[400],
            border: `1px dashed ${colors.grey[700]}`,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" color={colors.grey[200]}>
            No hay notificaciones para mostrar
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {notifications.map((n) => {
            const isUnread = !n.read;
            const id = n.id || n._id;
            return (
              <Paper
                key={id}
                onClick={() => handleNotificationClick(n)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  backgroundColor: isUnread ? colors.primary[500] : colors.primary[400],
                  border: `1px solid ${isUnread ? colors.greenAccent[400] : colors.grey[700]}`,
                  transition: "transform 120ms ease, box-shadow 120ms ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: theme.shadows[6],
                  },
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" fontWeight={800} color={colors.grey[100]}>
                        {n.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={isUnread ? "No leído" : "Leído"}
                        color={isUnread ? "secondary" : "default"}
                        variant={isUnread ? "filled" : "outlined"}
                      />
                    </Stack>
                    <Typography variant="body2" color={colors.grey[200]} mt={0.5}>
                      {n.message}
                    </Typography>
                    <Typography variant="caption" color={colors.grey[300]} mt={0.5} display="block">
                      {formatNotificationDate(n.createdAt)}
                    </Typography>
                  </Box>
                  {!n.read && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!id) return;
                        markNotificationAsRead(id)
                          .then(() => updateAsReadLocally(id))
                          .catch((err) => console.error("No se pudo marcar como leída", err));
                      }}
                    >
                      Marcar como leída
                    </Button>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default Notifications;
